import {
  Badge,
  Banner,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import produce from 'immer';
import type { Controls, InstrumentPanel, QuickView } from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { useCallback, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DeepWritable } from 'ts-essentials';

import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { mergePanelAndQuickViewControls } from '../../../../views/quickView/components/quickView/utils/mergePanelAndQuickViewControls';
import { type InstrumentPanelDescriptionProps } from '../panel/components/panel/components/description/instrumentPanelDescription';
import { InstrumentPanelTitle } from '../panel/components/panel/components/title/instrumentPanelTitle';

import styles from './analyticsPinPanelModal.module.scss';
import { QuickViewSelectionList } from './components/quickViewSelectionList/quickViewSelectionList';

const formId = 'analytics-pin-panel-form';

type FieldValues = {
  /**
   * The selected quick view.
   *
   * @note Represented as an array to be compatible with the `HookFormSelectionList` component.
   */
  quickView: QuickView[];
  /** An optional description for the panel being pinned. */
  description?: string;
};

/** A modal for pinning a panel to existing or new quick views in Analytics. */
export const AnalyticsPinPanelModal = createAppModal(
  { type: 'analytics:pinPanel' },
  ({ isOpen, modal, meta, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const navigate = useAppNavigate();
    const analyticsState = useAppSelector((state) => state.analytics);
    const { panel: initialPanel } = modal.arg;

    const form = useForm<FieldValues>({
      mode: 'all',
      defaultValues: {
        quickView: [],
        description: initialPanel.description,
      },
    });

    const [quickView] = form.watch('quickView');
    const description = form.watch('description');

    // Add the draft description to the panel object, so it can be displayed in the title.
    const panel = useMemo(
      () => ({ ...initialPanel, ...(description && { description }) }),
      [description, initialPanel],
    );

    // The dashboard where the panel originates from.
    const dashboard = analyticsApi.endpoints.readDashboards.useQueryState(
      undefined,
      {
        selectFromResult: ({ data }) =>
          data?.data.dashboards.find(({ id }) => id === panel.dashboard_id) ??
          analyticsState.dashboard!,
      },
    );

    const [updateQuickViewPanels] =
      analyticsApi.useUpdateQuickViewPanelsMutation();

    const handleCreateQuickView = async () => {
      const { quickView: createdQuickView } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'analytics:quickView',
            arg: {
              mode: 'create',
            },
          },
        }),
      );

      ampli.analyticsPinPanelQuickViewCreated({
        dashboard: dashboard.title,
        panel: panel.title,
        shared: createdQuickView.shared,
      });

      form.setValue('quickView', [createdQuickView], {
        shouldDirty: true,
        shouldValidate: true,
      });
    };

    const handleChangeDescription: NonNullable<
      InstrumentPanelDescriptionProps['onChange']
    > = useCallback(
      ({ description }) => {
        form.setValue('description', description, { shouldDirty: true });
      },
      [form],
    );

    const handleSubmit: SubmitHandler<FieldValues> = useCallback(
      async (values) => {
        const [quickView] = values.quickView;
        if (!quickView) return;

        try {
          const panelToPin: InstrumentPanel = produce(panel, (draftPanel) => {
            const quickViewControls = quickView.controls;
            if (!quickViewControls) return;

            draftPanel.controls = mergePanelAndQuickViewControls(
              draftPanel,
              quickViewControls,
            ) as DeepWritable<Controls>;
          });

          const {
            data: {},
          } = await updateQuickViewPanels({
            quickViewId: quickView.id,
            panels: [...quickView.panels, panelToPin],
          }).unwrap();

          meta?.resolve({ quickView });

          ampli.analyticsQuickViewPanelPinned({
            dashboard: dashboard.title,
            panel: panel.title,
            shared: quickView.shared,
            hasDescription: !!panel.description,
          });

          notify(
            {
              title: 'Success',
              text: 'The panel has been pinned to the dashboard.',
              icon: 'i-done',
              time: 5000,
              leftAction: {
                text: 'Go to the dashboard',
                action: () => {
                  navigate({
                    path: '/analytics/dashboard/custom/:id',
                    params: {
                      path: {
                        id: quickView.id,
                      },
                    },
                  });
                },
              },
            },
            NotificationType.Success,
          );

          hide?.();
        } catch (error) {
          notify(
            {
              title: 'Error while pinning the panel to the dashboard.',
            },
            NotificationType.Error,
          );
        }
      },
      [
        dashboard.title,
        hide,
        meta,
        navigate,
        notify,
        panel,
        updateQuickViewPanels,
      ],
    );

    const handleClose = useCallback(() => {
      meta?.reject({ isCancelled: true });
      hide?.();
    }, [hide, meta]);

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <ModalHead
          title={
            <InstrumentPanelTitle
              panel={panel}
              prefix={
                <Badge variant="primary" size="medium">
                  Pin
                </Badge>
              }
              onChangeDescription={handleChangeDescription}
              data-analytics-panel={panel.id}
            />
          }
          buttonsClassName={styles.modalHeadButtons}
          onClose={handleClose}
        />

        <ModalBody>
          <form
            id={formId}
            onSubmit={form.handleSubmit(handleSubmit)}
            noValidate
            tabIndex={-1}
          >
            <FormGrid>
              <FormField>
                <QuickViewSelectionList
                  name="quickView"
                  label="Saved dashboard"
                  control={form.control}
                  rules={{
                    required: 'Please select a dashboard',
                    validate: {
                      conflictingPanels: async ([quickView]) => {
                        if (!quickView) return true;

                        const hasConflicts = (quickView.panels ?? []).some(
                          ({ id }) => id === panel.id,
                        );

                        return (
                          !hasConflicts ||
                          'The selected dashboard already contains this panel. Please select another dashboard.'
                        );
                      },
                    },
                  }}
                  componentProps={{
                    placeholder:
                      'Select an existing dashboard or create a new one',
                    type: 'owned',
                    lazy: true,
                    suffix: {
                      type: 'button',
                      button: {
                        icon: 'plus',
                        onClick: handleCreateQuickView,
                        'aria-label': 'Create a new dashboard',
                      },
                    },
                  }}
                />

                {quickView?.controls?.period && (
                  <Banner
                    icon="calendar"
                    variant="warning"
                    headline="Period filter"
                    description={
                      <Text.Paragraph variant="4">
                        The selected dashboard has a <b>global period</b>{' '}
                        applied. If the panel you are pinning has a different
                        period, it will be adjusted to match the{' '}
                        <b>global period</b>, and the data may look different.
                      </Text.Paragraph>
                    }
                  />
                )}
              </FormField>
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFoot
          cancelOnClick={handleClose}
          confirmName="Confirm"
          confirmOnClick={noop}
          confirmProps={{
            type: 'submit',
            isLoading: form.formState.isSubmitting,
            disabled: !form.formState.isValid || form.formState.isSubmitting,
            form: formId,
          }}
        />
      </Modal>
    );
  },
);
